/**
 * Analytics Utility
 *
 * Simple, privacy-focused usage tracking for EmberBoard.
 * Tracks only anonymous user IDs, app version, and new vs returning users.
 *
 * Privacy features:
 * - Generates random UUID (no personal data)
 * - Stores user ID in localStorage
 * - Does NOT track in development/localhost
 * - No IP addresses, no cookies, no personal info
 * - Silent failure (doesn't break app if tracking fails)
 */

import { v4 as uuidv4 } from 'uuid';
import { APP_VERSION, ANALYTICS_ENABLED, ANALYTICS_ENDPOINT } from '@/constants/app';

const USER_ID_KEY = 'emberboard_user_id';
const FIRST_VISIT_KEY = 'emberboard_first_visit';

/**
 * Check if we're running in development/localhost
 * We don't want to track local development usage
 */
function isDevelopment(): boolean {
  // Check if running on localhost or common dev ports
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '';
  
  // Also check for common dev ports
  const port = window.location.port;
  const isDevPort = port === '5173' || port === '3000' || port === '8080';
  
  return isLocalhost || isDevPort;
}

/**
 * Get or create a unique anonymous user ID
 * Stored in localStorage to persist across sessions
 */
function getUserId(): string {
  // Check if user ID already exists
  let userId = localStorage.getItem(USER_ID_KEY);
  
  if (!userId) {
    // Generate new UUID for this user
    userId = uuidv4();
    localStorage.setItem(USER_ID_KEY, userId);
    
    // Mark this as their first visit
    localStorage.setItem(FIRST_VISIT_KEY, new Date().toISOString());
  }
  
  return userId;
}

/**
 * Check if this is a new user (first time visiting)
 */
function isNewUser(): boolean {
  const firstVisit = localStorage.getItem(FIRST_VISIT_KEY);
  
  if (!firstVisit) {
    return true; // No record = new user
  }
  
  // If first visit was within last 5 minutes, still count as "new"
  // This handles the case where tracking fails on first load
  const firstVisitTime = new Date(firstVisit).getTime();
  const now = Date.now();
  const fiveMinutes = 5 * 60 * 1000;
  
  return (now - firstVisitTime) < fiveMinutes;
}

/**
 * Send tracking event to analytics backend
 */
export async function trackAppUsage(): Promise<void> {
  // Check if analytics is enabled
  if (!ANALYTICS_ENABLED) {
    console.log('[Analytics] Analytics disabled in config');
    return;
  }
  
  // Don't track in development
  if (isDevelopment()) {
    console.log('[Analytics] Development mode detected - skipping tracking');
    return;
  }
  
  try {
    const userId = getUserId();
    const isNew = isNewUser();
    
    // Prepare tracking payload
    const payload = {
      userId,
      appVersion: APP_VERSION,
      isNewUser: isNew,
      timestamp: new Date().toISOString(),
    };
    
    console.log('[Analytics] Tracking usage:', { appVersion: APP_VERSION, isNewUser: isNew });
    
    // Send to analytics endpoint
    // Use fetch with no-cors to avoid CORS issues
    // We don't need the response, just send the data
    await fetch(ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      // Set a timeout so it doesn't hang the app
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });
    
    console.log('[Analytics] Usage tracked successfully');
  } catch (error) {
    // Silent failure - don't break the app if tracking fails
    // Just log to console for debugging
    console.warn('[Analytics] Failed to track usage (this is okay):', error);
  }
}

/**
 * Get analytics info for debugging (doesn't send anything)
 */
export function getAnalyticsInfo(): {
  userId: string;
  isNewUser: boolean;
  appVersion: string;
  isDevelopment: boolean;
  firstVisit: string | null;
} {
  return {
    userId: getUserId(),
    isNewUser: isNewUser(),
    appVersion: APP_VERSION,
    isDevelopment: isDevelopment(),
    firstVisit: localStorage.getItem(FIRST_VISIT_KEY),
  };
}
